import React from "react";
import styles from "./PlaySong.module.css";
import { gsap, Power4 } from "gsap";
import userDataContext from "../../store/userData-context";
import Player from "./Player/Player";
import AudioElement from "./Player/AudioElement";

interface song {
  img: string;
  name: string;
  artist: string;
  desc: string;
  id: string;
  mp3: string;
}

export default function PlaySong() {
  const userDataCtx = React.useContext(userDataContext);
  const [songs, setSongs] = React.useState<song[]>([]);
  const [shuffledSongs, setShuffledSongs] = React.useState<song[]>([]);
  const [songData, setSongData] = React.useState({
    img: "",
    name: "",
    artist: "",
    desc: "",
    id: "",
    mp3: "",
  });
  const [songNum, setSongNum] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [shouldRepeat, setShouldRepeat] = React.useState(false);
  const [isShuffling, setIsShuffling] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  React.useEffect(() => {
    setSongs(userDataCtx.currentPlaylist.songs);

    if (userDataCtx.currentPlaylist.songs.length === 0) {
      setSongData(userDataCtx.song);
    }

    resetPlayer();
  }, [userDataCtx.song, userDataCtx.currentPlaylist]);

  React.useLayoutEffect(() => {
    gsap.from(`.${styles.container}`, {
      y: window.innerHeight + 300,
      opacity: 0,
      duration: 1,
      ease: Power4.easeOut,
    });
  }, []);

  React.useEffect(() => {
    setIsPlaying(!userDataCtx.pause);
  }, [userDataCtx.pause]);

  function resetPlayer() {
    setIsShuffling(false);
    setIsPlaying(true);
    setShouldRepeat(false);
    setSongNum(0);
  }

  function shuffle() {
    if (userDataCtx.currentPlaylist.songs.length <= 1) return;
    setIsShuffling((prev) => !prev);

    const unshuffled = [...songs];
    const shuffled: song[] = [];

    for (let i = 0; i < songs.length; i++) {
      let randomNum = Math.floor(Math.random() * unshuffled.length);
      const spliced = unshuffled.splice(randomNum, randomNum + 1);
      shuffled.push(...spliced);
    }

    if (shuffled[songNum].name === songs[songNum].name) {
      shuffle();
      return;
    }

    setShuffledSongs(shuffled);
  }

  function checkNextSong() {
    if (songNum + 1 < songs.length) {
      setSongNum((prev) => (prev += 1));
    } else {
      setIsPlaying(false);
    }
  }

  React.useEffect(() => {
    if (songs.length > 0) {
      if (isShuffling) {
        setSongData(shuffledSongs[songNum]);
      } else {
        setSongData(songs[songNum]);
      }
    }
  }, [songNum, songs, shuffledSongs, isShuffling]);

  function onPlaying() {
    const duration = audioRef.current?.duration;
    const currentTime = audioRef.current?.currentTime;

    if (duration && currentTime) {
      setSongData((prev) => ({
        ...prev,
        progress: ((currentTime / duration) * 100).toFixed(2),
        length: duration,
      }));
    }

    if (duration === currentTime) {
      if (shouldRepeat) {
        audioRef.current!.currentTime = 0;
        audioRef.current!.play();
      } else if (songs.length > 0) {
        checkNextSong();
      } else {
        setIsPlaying(false);
      }
    }
  }

  function changeSong(direction: string) {
    if (direction === "forward") {
      if (songNum < songs.length - 1) {
        setSongNum((prev) => (prev += 1));
      } else setSongNum(0);
    } else {
      if (songNum === 0) {
        setSongData((prev) => ({
          ...prev,
          progress: 0,
        }));
        audioRef.current!.currentTime = 0;
      } else {
        setSongNum((prev) => (prev -= 1));
      }
    }
    setIsPlaying(true);
    setShouldRepeat(false);
  }

  return (
    <section className={styles.container}>
      <AudioElement
        src={songData.mp3}
        refPoint={audioRef}
        onPlaying={onPlaying}
        isPlaying={isPlaying}
      />
      <Player
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        audioRef={audioRef}
        songData={songData}
        changeSong={changeSong}
        setShouldRepeat={setShouldRepeat}
        shouldRepeat={shouldRepeat}
        shuffle={shuffle}
        isShuffling={isShuffling}
      />
    </section>
  );
}
